// PDF export of GooseBerry markdown using jsPDF (text-only, structured by H2 sections).
// Broken into small helpers so the top-level export function stays simple and testable.
import { jsPDF } from "jspdf";
import { parseGooseBerryMarkdown } from "@/lib/markdownParser";

const MARGIN = 48;
const GREEN = { r: 0x61, g: 0xCB, b: 0xA3 };
const LINE_H = 14;

function stripMd(text) {
  if (!text) return "";
  let t = text;
  t = t.replace(/\[([^\]]+)\]\(([^)]+)\)/g, "$1 ($2)");
  t = t.replace(/\*\*([^*]+)\*\*/g, "$1");
  t = t.replace(/\*([^*]+)\*/g, "$1");
  t = t.replace(/`([^`]+)`/g, "$1");
  t = t.replace(/^#{1,6}\s+/gm, "");
  t = t.replace(/^\s*[-*]\s+/gm, "\u2022 ");
  return t;
}

function _makeEnsureSpace(doc, pageHeight, yRef) {
  return (needed) => {
    if (yRef.y + needed > pageHeight - MARGIN) {
      doc.addPage();
      yRef.y = MARGIN;
    }
  };
}

function _renderHeader(doc, projectName, mode, yRef, pageWidth, language = "en") {
  const nl = language === "nl";
  doc.setFont("helvetica", "bold");
  doc.setFontSize(20);
  doc.setTextColor(GREEN.r, GREEN.g, GREEN.b);
  doc.text("GooseBerry", MARGIN, yRef.y);
  yRef.y += 22;

  doc.setFontSize(13);
  doc.setTextColor(0, 0, 0);
  doc.text(projectName ? `Project: ${projectName}` : nl ? "Project: (naamloos)" : "Project: (untitled)", MARGIN, yRef.y);
  yRef.y += 14;

  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  doc.setTextColor(85, 85, 85);
  const dateStr = new Date().toLocaleString();
  doc.text(`${nl ? "Modus" : "Mode"}: ${mode || "feedback"}, ${nl ? "Gegenereerd" : "Generated"}: ${dateStr}`, MARGIN, yRef.y);
  yRef.y += 8;

  doc.setDrawColor(220, 220, 220);
  doc.line(MARGIN, yRef.y + 8, pageWidth - MARGIN, yRef.y + 8);
  yRef.y += 24;
}

function _renderSection(doc, section, yRef, contentWidth, ensureSpace, language = "en") {
  ensureSpace(36);

  doc.setFont("helvetica", "bold");
  doc.setFontSize(13);
  doc.setTextColor(0, 0, 0);
  const head = section.confidence ? `${section.title}, ${language === "nl" ? "Vertrouwen" : "Confidence"}: ${section.confidence}` : section.title;
  doc.text(head, MARGIN, yRef.y);
  yRef.y += 16;

  doc.setDrawColor(GREEN.r, GREEN.g, GREEN.b);
  doc.setLineWidth(0.6);
  doc.line(MARGIN, yRef.y - 6, MARGIN + Math.min(contentWidth, 80), yRef.y - 6);

  doc.setFont("helvetica", "normal");
  doc.setFontSize(11);
  doc.setTextColor(20, 20, 20);
  const body = stripMd(section.body || "").trim();
  if (body) {
    const lines = doc.splitTextToSize(body, contentWidth);
    for (const ln of lines) {
      ensureSpace(LINE_H);
      doc.text(ln, MARGIN, yRef.y);
      yRef.y += LINE_H;
    }
  }
  yRef.y += 10;
}

function _renderSourceItem(doc, src, yRef, contentWidth, ensureSpace) {
  ensureSpace(28);
  const head = `[${src.source || "Other"}] ${src.title || ""}`;
  const headLines = doc.splitTextToSize(head, contentWidth);
  doc.setFont("helvetica", "bold");
  for (const hl of headLines) {
    ensureSpace(13);
    doc.text(hl, MARGIN, yRef.y);
    yRef.y += 13;
  }
  doc.setFont("helvetica", "normal");
  const desc = stripMd(src.description || "");
  if (desc) {
    const dl = doc.splitTextToSize(desc, contentWidth);
    for (const ln of dl) { ensureSpace(12); doc.text(ln, MARGIN, yRef.y); yRef.y += 12; }
  }
  if (src.url) {
    ensureSpace(12);
    doc.setTextColor(GREEN.r, GREEN.g, GREEN.b);
    doc.textWithLink(src.url, MARGIN, yRef.y, { url: src.url });
    doc.setTextColor(20, 20, 20);
    yRef.y += 14;
  }
  yRef.y += 6;
}

function _renderSourcesAppendix(doc, sourcesUsed, yRef, contentWidth, ensureSpace, language = "en") {
  if (!Array.isArray(sourcesUsed) || sourcesUsed.length === 0) return;
  const nl = language === "nl";
  ensureSpace(40);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(13);
  doc.setTextColor(GREEN.r, GREEN.g, GREEN.b);
  doc.text(nl ? "Gebruikte databronnen (alleen contextuele onderbouwing)" : "Data Sources Used (grounding only)", MARGIN, yRef.y);
  yRef.y += 16;

  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  doc.setTextColor(20, 20, 20);
  for (const s of sourcesUsed.slice(0, 10)) {
    _renderSourceItem(doc, s, yRef, contentWidth, ensureSpace);
  }
}

function _renderFooter(doc, yRef, ensureSpace, language = "en") {
  ensureSpace(20);
  doc.setFont("helvetica", "italic");
  doc.setFontSize(9);
  doc.setTextColor(120, 120, 120);
  doc.text(
    language === "nl"
      ? "Bronnen en lokale codebooks zijn alleen gebruikt als contextuele onderbouwing; raw paneldata en datasets met beperkte toegang zijn niet geanalyseerd."
      : "Sources and local codebooks were used as grounding only; raw panel and permission-restricted datasets were not analyzed.",
    MARGIN,
    yRef.y
  );
}

export function exportGooseBerryPdf({ markdown, projectName, mode, sourcesUsed, language = "en" }) {
  const doc = new jsPDF({ unit: "pt", format: "a4" });
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const contentWidth = pageWidth - MARGIN * 2;
  const yRef = { y: MARGIN };
  const ensureSpace = _makeEnsureSpace(doc, pageHeight, yRef);

  _renderHeader(doc, projectName, mode, yRef, pageWidth, language);

  const parsed = parseGooseBerryMarkdown(markdown);
  for (const section of parsed.sections) {
    _renderSection(doc, section, yRef, contentWidth, ensureSpace, language);
  }

  _renderSourcesAppendix(doc, sourcesUsed, yRef, contentWidth, ensureSpace, language);
  _renderFooter(doc, yRef, ensureSpace, language);

  const filename = `gooseberry-${(projectName || "report").toLowerCase().replace(/\s+/g, "-")}.pdf`;
  doc.save(filename);
}
